/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Form } from 'react-router';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

// Runtime SDK
import { buildUrl } from '@salesforce/storefront-next-runtime/site-context';
import { useConfig } from '@salesforce/storefront-next-runtime/config';

// Hooks
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';

// Lib
import { cn } from '@/lib/utils';

// Assets
import warningIcon from '/images/warning.svg';

interface LogoutConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function LogoutConfirmDialog({ open, onOpenChange }: LogoutConfirmDialogProps): ReactElement {
    const { t: tAccount } = useTranslation('account');
    const config = useConfig();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();

    const buttonClass = 'w-full sm:w-[225.5px] h-[42px] rounded-[32px] flex items-center justify-center px-4 text-sm font-semibold transition-colors cursor-pointer';

    return (
        <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <AlertDialogPrimitive.Portal>
                <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
                <AlertDialogPrimitive.Content
                    className={cn(
                        'fixed top-1/2 left-1/2 z-50 w-[255px] min-h-[318px] sm:w-[497px] sm:min-h-[282px] max-w-[calc(100%-2rem)]',
                        '-translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white',
                        'pt-8 pr-3 pb-8 pl-3 flex flex-col items-center gap-6 sm:gap-6 shadow-xl',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
                        'data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
                    )}
                >
                    <AlertDialogPrimitive.Cancel asChild>
                        <button
    type="button"
    aria-label={tAccount('logoutConfirm.cancel')}
    className="absolute top-[7px] right-[7px] h-10 w-10 flex items-center justify-center text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
>
    <X className="h-6 w-6 text-black" strokeWidth={1} />
</button>

                    </AlertDialogPrimitive.Cancel>

                    <img src={warningIcon} alt="" className="h-14 w-14 shrink-0" />

                    <div className="flex flex-col items-center gap-2 shrink-0">
                        <div className="flex w-[177px] h-[36px] items-center justify-center text-center">
                            <AlertDialogPrimitive.Title className="font-georama text-xl font-bold tracking-normal [font-variant:small-caps] text-neutral-900">
                                {tAccount('logoutConfirm.title')}
                            </AlertDialogPrimitive.Title>
                        </div>

                        <AlertDialogPrimitive.Description className="text-sm font-normal leading-5 tracking-normal text-[#262626] text-center">
                            {tAccount('logoutConfirm.description')}
                        </AlertDialogPrimitive.Description>
                    </div>

                    <div className="flex w-full flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 shrink-0">
                        <Form
                            method="post"
                            className="w-full sm:w-auto"
                            action={buildUrl({
                                to: '/logout',
                                urlConfig: config.url,
                                params: { siteId: siteRef, localeId: localeRef },
                            })}
                        >
                            <button
                                type="submit"
                                className={cn(buttonClass, 'bg-white border border-black text-neutral-900 hover:bg-neutral-50')}
                            >
                                {tAccount('logoutConfirm.confirm')}
                            </button>
                        </Form>
                        <AlertDialogPrimitive.Cancel asChild>
                            <button
                                type="button"
                                className={cn(buttonClass, 'bg-neutral-900 text-white hover:bg-neutral-800')}
                            >
                                {tAccount('logoutConfirm.cancel')}
                            </button>
                        </AlertDialogPrimitive.Cancel>
                    </div>
                </AlertDialogPrimitive.Content>
            </AlertDialogPrimitive.Portal>
        </AlertDialogPrimitive.Root>
    );
}
